import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
// import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  ArrowLeft,
  Save,
  Check,
  AlertTriangle,
  Users,
  FileText,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  getEvent,
  listEventParticipants,
  listUsers,
} from "../../graphql/queries";
import { createEventStanding } from "../../graphql/mutations";
import { parseStandingsReport } from "../../lib/standingsImport";

const client = generateClient();

interface EventLinkRow {
  rank: number;
  playerName: string;
  points: number;
  omw: number; // Opponents Match Win %
  gw: number; // Game Win %
  ogw: number; // Opponents Game Win %
  email?: string;
  username?: string;
}

interface UserMatch {
  eventLinkRow: EventLinkRow;
  matchedUser: any | null;
  confidence: "exact" | "partial" | "none";
  needsManualMatch: boolean;
}

interface StandingEntry {
  userId: string;
  rank: number;
  points: number;
  omw: number;
  gw: number;
  ogw: number;
  playerName: string;
}

const EventStandingsImport: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [eventLinkData, setEventLinkData] = useState<EventLinkRow[]>([]);
  const [userMatches, setUserMatches] = useState<UserMatch[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Record<number, string>>(
    {}
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<"upload" | "match" | "review">("upload");

  // Fetch event details
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch all users for matching
  const {
    data: allUsers,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["allUsers"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return result.data.listUsers.items;
    },
  });

  // Fetch current participants (for reference)
  const {
    data: participants,
    isLoading: participantsLoading,
    error: participantsError,
  } = useQuery({
    queryKey: ["eventParticipants", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventParticipants,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventParticipants.items;
    },
    enabled: !!eventId,
  });

  // Parse EventLink PDF data manually
  const parseEventLinkData = useCallback((text: string): EventLinkRow[] => {
    const report = parseStandingsReport(text);
    return (report?.results || []).map((result) => ({
      rank: result.rank,
      playerName: result.name,
      points: result.points,
      omw: result.omw,
      gw: result.gw,
      ogw: result.ogw,
    }));
  }, []);

  // Handle EventLink data input
  const [eventLinkText, setEventLinkText] = useState<string>("");

  const handleEventLinkDataSubmit = () => {
    if (!eventLinkText.trim()) {
      alert("Please paste the EventLink standings data.");
      return;
    }

    setIsProcessing(true);
    try {
      const data = parseEventLinkData(eventLinkText);
      if (data.length === 0) {
        alert("No valid standings data found. Please check the format.");
        return;
      }
      setEventLinkData(data);
      setStep("match");
    } catch (error) {
      console.error("Error parsing EventLink data:", error);
      alert("Error parsing EventLink data. Please check the format.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Match users with EventLink data
  const matchUsers = useCallback(() => {
    if (!eventLinkData || !allUsers) return;

    const matches: UserMatch[] = eventLinkData.map((row) => {
      let bestMatch = null;
      let confidence: "exact" | "partial" | "none" = "none";

      // Try exact email match first
      if (row.email) {
        const emailMatch = allUsers.find(
          (user: any) => user.email?.toLowerCase() === row.email?.toLowerCase()
        );
        if (emailMatch) {
          bestMatch = emailMatch;
          confidence = "exact";
        }
      }

      // Try exact username match
      if (!bestMatch && row.username) {
        const usernameMatch = allUsers.find(
          (user: any) =>
            user.username?.toLowerCase() === row.username?.toLowerCase()
        );
        if (usernameMatch) {
          bestMatch = usernameMatch;
          confidence = "exact";
        }
      }

      // Try partial name matching
      if (!bestMatch) {
        const nameMatch = allUsers.find((user: any) => {
          const userName = (
            user.displayName ||
            user.username ||
            ""
          ).toLowerCase();
          const csvName = row.playerName.toLowerCase();
          return userName.includes(csvName) || csvName.includes(userName);
        });
        if (nameMatch) {
          bestMatch = nameMatch;
          confidence = "partial";
        }
      }

      return {
        eventLinkRow: row,
        matchedUser: bestMatch,
        confidence,
        needsManualMatch: !bestMatch,
      };
    });

    setUserMatches(matches);
  }, [eventLinkData, allUsers]);

  // Run matching when data is available
  React.useEffect(() => {
    if (eventLinkData.length > 0 && allUsers && step === "match") {
      matchUsers();
    }
  }, [eventLinkData, allUsers, step, matchUsers]);

  // Handle manual user selection
  const handleUserSelection = (csvIndex: number, userId: string) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [csvIndex]: userId,
    }));
  };

  // Get final standings for review
  const getFinalStandings = (): StandingEntry[] => {
    return userMatches
      .map((match, index) => {
        const selectedUserId = selectedUsers[index];
        const userId = selectedUserId || match.matchedUser?.id;

        return {
          userId: userId || "",
          rank: match.eventLinkRow.rank,
          points: match.eventLinkRow.points,
          omw: match.eventLinkRow.omw,
          gw: match.eventLinkRow.gw,
          ogw: match.eventLinkRow.ogw,
          playerName: match.eventLinkRow.playerName,
        };
      })
      .filter((standing) => standing.userId); // Only include matched users
  };

  // Save standings mutation
  const saveStandingsMutation = useMutation({
    mutationFn: async (standingsData: StandingEntry[]) => {
      const promises = standingsData.map((standing) => {
        return client.graphql({
          query: createEventStanding,
          variables: {
            input: {
              eventId: eventId!,
              userId: standing.userId,
              rank: standing.rank,
              points: standing.points,
              omw: standing.omw,
              gw: standing.gw,
              ogw: standing.ogw,
            },
          },
        });
      });

      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventStandings", eventId] });
      setIsSubmitting(false);
      alert("Standings imported successfully!");
      navigate(`/admin/events/${eventId}/standings`);
    },
    onError: (error: any) => {
      console.error("Error saving standings:", error);
      setIsSubmitting(false);
      alert("Failed to save standings. Please try again.");
    },
  });

  const handleSaveStandings = () => {
    const finalStandings = getFinalStandings();
    if (finalStandings.length === 0) {
      alert(
        "No valid standings to save. Please match at least one user first."
      );
      return;
    }

    const unmatchedStandings = userMatches.filter(
      (match) =>
        !match.matchedUser && !selectedUsers[userMatches.indexOf(match)]
    );

    if (unmatchedStandings.length > 0) {
      const confirmSave = window.confirm(
        `You have ${unmatchedStandings.length} unmatched players. Do you want to save the ${finalStandings.length} matched standings now? You can assign the remaining players later.`
      );
      if (!confirmSave) return;
    }

    setIsSubmitting(true);
    saveStandingsMutation.mutate(finalStandings);
  };

  const getConfidenceBadge = (confidence: string) => {
    switch (confidence) {
      case "exact":
        return (
          <Badge variant="default" className="bg-green-500">
            Exact Match
          </Badge>
        );
      case "partial":
        return (
          <Badge variant="secondary" className="bg-yellow-500">
            Partial Match
          </Badge>
        );
      case "none":
        return <Badge variant="destructive">No Match</Badge>;
      default:
        return null;
    }
  };

  const unmatchedCount = userMatches.filter(
    (match) => !match.matchedUser && !selectedUsers[userMatches.indexOf(match)]
  ).length;
  const matchedCount = userMatches.length - unmatchedCount;

  if (eventLoading || usersLoading || participantsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (eventError || usersError || participantsError || !event) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">Error loading event data</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/events/${eventId}/standings`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Standings
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Import EventLink Standings</h1>
          <p className="text-muted-foreground mt-2">
            Paste EventLink PDF standings data for {event.name}
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className={`flex items-center gap-2 ${step === "upload" ? "text-primary" : "text-muted-foreground"}`}
        >
          <FileText className="h-5 w-5" />
          <span>1. Paste Data</span>
        </div>
        <div className="h-px w-8 bg-border" />
        <div
          className={`flex items-center gap-2 ${step === "match" ? "text-primary" : "text-muted-foreground"}`}
        >
          <Users className="h-5 w-5" />
          <span>2. Match Users</span>
        </div>
        <div className="h-px w-8 bg-border" />
        <div
          className={`flex items-center gap-2 ${step === "review" ? "text-primary" : "text-muted-foreground"}`}
        >
          <Check className="h-5 w-5" />
          <span>3. Review & Save</span>
        </div>
      </div>

      {/* Step 1: Paste EventLink Data */}
      {step === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle>Paste EventLink Standings</CardTitle>
            <CardDescription>
              Copy and paste the standings data from your EventLink PDF report
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eventlink-data">EventLink Standings Data</Label>
              <textarea
                id="eventlink-data"
                className="w-full h-64 p-4 border rounded-lg font-mono text-sm"
                placeholder="Paste your EventLink standings data here...
Example:
1      Matt Dodds              12     56     72     53    
2      Luke Del Bianco         9      58     63     55    
3      Alexistaylor Vega       6      64     54     58"
                value={eventLinkText}
                onChange={(e) => setEventLinkText(e.target.value)}
                disabled={isProcessing}
              />
            </div>

            {isProcessing && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
                Processing EventLink data...
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleEventLinkDataSubmit}
                disabled={isProcessing || !eventLinkText.trim()}
                className="flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Process Standings
              </Button>
            </div>

            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <div>
                  <strong>Instructions:</strong> Copy the standings table from
                  your EventLink PDF report and paste it above. The system will
                  automatically parse the Rank, Name, Points, OMW%, GW%, and
                  OGW% data.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Match Users */}
      {step === "match" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Match Players to Users</CardTitle>
                  <CardDescription>
                    Review and confirm user matches for {eventLinkData.length}{" "}
                    players
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm">
                    <span className="text-green-600">
                      {matchedCount} matched
                    </span>
                    {unmatchedCount > 0 && (
                      <span className="text-red-600 ml-2">
                        {unmatchedCount} unmatched
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setStep("review")}
                      disabled={unmatchedCount > 0}
                    >
                      Continue to Review
                    </Button>
                    {unmatchedCount > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => setStep("review")}
                      >
                        Save Matched ({matchedCount})
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          <div className="space-y-4">
            {userMatches.map((match, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <div className="text-lg font-bold">
                          #{match.eventLinkRow.rank}
                        </div>
                        <div>
                          <div className="font-medium">
                            {match.eventLinkRow.playerName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {match.eventLinkRow.points} points • OMW:{" "}
                            {match.eventLinkRow.omw}% • GW:{" "}
                            {match.eventLinkRow.gw}% • OGW:{" "}
                            {match.eventLinkRow.ogw}%
                          </div>
                        </div>
                        {getConfidenceBadge(match.confidence)}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {match.matchedUser ? (
                        <div className="text-sm">
                          <div className="font-medium">
                            {match.matchedUser.displayName ||
                              match.matchedUser.username}
                          </div>
                          <div className="text-muted-foreground">
                            {match.matchedUser.email}
                          </div>
                        </div>
                      ) : (
                        <Select
                          value={selectedUsers[index] || ""}
                          onValueChange={(value) =>
                            handleUserSelection(index, value)
                          }
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue placeholder="Select user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allUsers?.map((user: any) => (
                              <SelectItem key={user.id} value={user.id}>
                                <div className="flex flex-col">
                                  <span>
                                    {user.displayName || user.username}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {user.email}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Review & Save */}
      {step === "review" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Review Final Standings</CardTitle>
                  <CardDescription>
                    Review the standings before saving to the database
                  </CardDescription>
                </div>
                <Button
                  onClick={handleSaveStandings}
                  disabled={isSubmitting}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isSubmitting ? "Saving..." : "Save Standings"}
                </Button>
              </div>
            </CardHeader>
          </Card>

          <div className="space-y-4">
            {getFinalStandings().map((standing, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-lg font-bold">#{standing.rank}</div>
                      <div>
                        <div className="font-medium">{standing.playerName}</div>
                        <div className="text-sm text-muted-foreground">
                          {standing.points} points • OMW: {standing.omw}% • GW:{" "}
                          {standing.gw}% • OGW: {standing.ogw}%
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      User ID: {standing.userId.slice(-8)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventStandingsImport;
